import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import AutoCompleteField from '@components/common/form/AutoCompleteField';
import ColorPickerField from '@components/common/form/ColorPickerField';
import ImagePickerModalField from '@components/common/form/ImagePickerModal/ImagePickerModalField';
import KOverlapSlider from '@components/common/form/KOverlapSlider';
import { AppConstants, PRINTER_MAPPING_MEDIA_KIND } from '@constants';
import apiConfig from '@constants/apiConfig';
import { statusOptions } from '@constants/masterData';
import useBasicForm from '@hooks/useBasicForm';
import useFetch from '@hooks/useFetch';
import useTranslate from '@hooks/useTranslate';
import { BaseForm, NumericField, SelectField } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { getBase64, parseSettings } from '@utils';
import { Card, Checkbox, Col, Flex, Form, Row, Switch } from 'antd';

const PrinterMappingForm = ({ formId, actions, dataDetail, onSubmit, setIsChangedFormValues, isEditing, printerId, deviceId }) => {
    const translate = useTranslate();
    const statusValues = translate.formatKeys(statusOptions, ['label']);
    const [imageUrl, setImageUrl] = useState(null);
    const [selectedValue, setSelectedValue] = useState(null);
    const [printerNumberOptions, setPrinterNumberOptions] = useState([]);
    const [isRandomColor, setIsRandomColor] = useState(false);

    const { form, mixinFuncs, onValuesChange } = useBasicForm({
        onSubmit,
        setIsChangedFormValues,
    });

    const { execute: executePrinter } = useFetch(apiConfig.printer.notExist);

    const location = useLocation();
    const params = useParams();
    const { restaurantId } = useParams();

    const resolvedPrinterId = printerId ?? params.printerId;
    const handleSubmit = (values) => {
        const settings = JSON.stringify({
            prepare_time: values.prepareTime ? Number(values.prepareTime) : undefined,
            k_overlap: values.kOverlap !== undefined ? Number(values.kOverlap) : undefined,
            color: isRandomColor ? '-1' : values.color,
            show_price: values.showPrice ? 1 : 0,
            show_plu: !!values.showPlu,
            sort_plu: !!values.sortPlu,
            display_food_by_customer: !!values.displayFoodByCustomer,
        });
        const { showPrice, prepareTime, color, kOverlap, showPlu, sortPlu, displayFoodByCustomer, ...restValues } = values;
        return mixinFuncs.handleSubmit({
            ...restValues,
            settings,
            image: imageUrl,
            printerId: Number(resolvedPrinterId),
        });
    };
    const uploadFile = (file, onSuccess, onError) => {
        getBase64(file, (base64Image) => {
            if (base64Image.startsWith('data:image')) {
                setImageUrl(base64Image);
            } else {
                setImageUrl(`data:image/png;base64,${base64Image}`);
            }
        });
    };

    useEffect(() => {
        const parsedSettings = parseSettings(dataDetail?.settings);
        const isRandom = parsedSettings?.color === '-1';
        setIsRandomColor(isRandom);
        form.setFieldsValue({
            ...dataDetail,
            showPrice: parsedSettings?.show_price === 1,
            printerGroupId: dataDetail?.printerGroup?.id,
            prepareTime: parsedSettings?.prepare_time,
            color: parsedSettings?.color,
            kOverlap: parsedSettings?.k_overlap ?? 0,
            showPlu: !!parsedSettings?.show_plu,
            sortPlu: !!parsedSettings?.sort_plu,
            displayFoodByCustomer: parsedSettings?.display_food_by_customer ?? true,
        });
        setImageUrl(dataDetail?.image);
        setSelectedValue(dataDetail?.printerNumber || null);
    }, [dataDetail]);

    useEffect(() => {
        if (!isEditing) {
            form.setFieldsValue({
                status: statusValues[0].value,
                showPrice: false,
                sortPlu: true,
                displayFoodByCustomer: true,
            });
        }
    }, [isEditing]);

    useEffect(() => {
        if (deviceId) {
            executePrinter({
                params: {
                    parentId: deviceId,
                },
                onCompleted: (res) => {
                    setPrinterNumberOptions(res.data || []);
                },
            });
        }
    }, [deviceId]);

    return (
        <BaseForm id={formId} onFinish={handleSubmit} form={form} onValuesChange={onValuesChange}>
            <Card className="card-form" bordered={false}>
                <ImagePickerModalField
                    name="image"
                    onChange={(url) => {
                        setImageUrl(url);
                        setIsChangedFormValues(true);
                    }}
                    imageUrl={imageUrl}
                    imagesApiConfig={apiConfig.file.getImages}
                    imagesParams={{ kind: PRINTER_MAPPING_MEDIA_KIND }}
                    onUploadFile={uploadFile}
                    value={imageUrl}
                    imagesRootUrl={AppConstants.templateRootUrl} 
                />
                <Row gutter={10} align="middle">
                    <Col span={12}>
                        <AutoCompleteField
                            label={<FormattedMessage defaultMessage="Process" />}
                            name={['printerGroupId']}
                            apiConfig={apiConfig.printerGroup.getList}
                            mappingOptions={(item) => ({ value: item.id, label: item.name })}
                            initialSearchParams={{ restaurantId: restaurantId }}
                            searchParams={(text) => ({ name: text, restaurantId: restaurantId })}
                            required
                        />
                    </Col>
                    <Col span={12}>
                        <SelectField
                            label={translate.formatMessage(commonMessage.printerNumber)}
                            name="printerNumber"
                            required
                            options={[
                                ...printerNumberOptions
                                    .filter(
                                        (item) => item === selectedValue || !selectedValue || item !== selectedValue,
                                    )
                                    .map((item) => ({
                                        value: item,
                                        label: `${translate.formatMessage(commonMessage.printerNumber)} ${item}`,
                                    })),
                                ...(selectedValue && !printerNumberOptions.includes(selectedValue)
                                    ? [
                                        {
                                            value: selectedValue,
                                            label: `${translate.formatMessage(commonMessage.printerNumber)} ${selectedValue}`,
                                        },
                                    ]
                                    : []),
                            ].sort((a, b) => a.value - b.value)}
                            onChange={(value) => {
                                if (!printerNumberOptions.includes(dataDetail?.printerNumber)) {
                                    setPrinterNumberOptions([...printerNumberOptions, dataDetail?.printerNumber]);
                                }
                                setSelectedValue(value);
                            }}
                            allowClear={false}
                        />
                    </Col>
                    <Col span={12}>
                        <NumericField
                            label={translate.formatMessage(commonMessage.prepareTime) + ` (${translate.formatMessage(commonMessage.minute)})`}
                            name="prepareTime"
                            required
                            formatter={(v) => v}
                            parser={(v) => v}
                            rules={[ { pattern: /^[0-9]+$/, message: '' } ]}
                            width="100%"
                        />
                    </Col>
                    <Col span={12}>
                        <Flex align="center" gap={16} style={{ marginTop: 4 }}>
                            <Form.Item
                                name="showPrice"
                                valuePropName="checked"
                                noStyle
                                required
                            >
                                <Switch
                                    onChange={(value) => form.setFieldsValue({ showPrice: value })}
                                />
                            </Form.Item>
                            <span>{translate.formatMessage(commonMessage.showPrice)}</span>
                        </Flex>
                    </Col>
                </Row>
                <Row gutter={[10, 20]} style={{ margin: '12px 0 24px' }}>
                    <Col span={12}>
                        <Flex align="center" gap={16}>
                            <Form.Item
                                name="showPlu"
                                valuePropName="checked"
                                noStyle
                            >
                                <Switch />
                            </Form.Item>
                            <span>{translate.formatMessage(commonMessage.showPLU)}</span>
                        </Flex>
                    </Col>
                    <Col span={12}>
                        <Flex align="center" gap={16}>
                            <Form.Item
                                name="sortPlu"
                                valuePropName="checked"
                                noStyle
                            >
                                <Switch />
                            </Form.Item>
                            <span>{translate.formatMessage(commonMessage.sortPLU)}</span>
                        </Flex>
                    </Col>
                    <Col span={12}>
                        <Flex align="center" gap={16}>
                            <Form.Item
                                name="displayFoodByCustomer"
                                valuePropName="checked"
                                noStyle
                            >
                                <Switch />
                            </Form.Item>
                            <span>{translate.formatMessage(commonMessage.displayFoodByCustomer)}</span>
                        </Flex>
                    </Col>
                </Row>
                <Row gutter={10} align="middle">
                    <Col span={12}>
                        <Flex align="center" gap={12}>
                            <div>
                                <ColorPickerField
                                    name={['color']}
                                    label={<FormattedMessage defaultMessage="Color" />}
                                    fieldProps={{ swatchWidth: 160, disabled: isRandomColor }}
                                    enableAlpha={false}
                                />
                            </div>
                            <div style={{ marginTop: 6 }}>
                                <Checkbox
                                    checked={isRandomColor}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setIsRandomColor(checked);
                                        setIsChangedFormValues(true);
                                        form.setFieldsValue({
                                            color: checked ? null : '#1677ff', 
                                        });
                                    }}
                                >
                                    {translate.formatMessage(commonMessage.randomColor)}
                                </Checkbox>
                            </div>
                        </Flex>
                    </Col>
                    <Col span={12}>
                        <Form.Item label={translate.formatMessage(commonMessage.kOverlap)} name="kOverlap" >
                            <KOverlapSlider />
                        </Form.Item>
                    </Col>
                </Row>

                <div className="footer-card-form">{actions}</div>
            </Card>
        </BaseForm>
    );
};

export default PrinterMappingForm;
